import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { motion } from 'framer-motion';
import Button from '../components/Button';
import Input from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import Navbar from '../components/Navbar';
import Modal from '../components/ui/modal';
import { Ship } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Clientes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientesData, setClientesData] = useState({
    clientes: [],
    totalClientes: 0,
    totalPages: 1,
    currentPage: 1
  });
  const [selectedClientBoats, setSelectedClientBoats] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [filterBoatsInWater, setFilterBoatsInWater] = useState(false);
  const clientsPerPage = 21;

  const fetchClientes = async (page = 1, search = '') => {
    try {
      const response = await api.get('/clientes', {
        params: {
          page,
          limit: clientsPerPage,
          search,
          inWater: filterBoatsInWater
        }
      });
      
      // Validación robusta de la respuesta
      const data = response.data || {};
      const clientesArray = Array.isArray(data.clientes) ? data.clientes : [];
      
      setClientesData({
        clientes: clientesArray,
        totalClientes: data.totalClientes || 0,
        totalPages: data.totalPages || 1,
        currentPage: data.currentPage || 1
      });
    } catch (error) {
      console.error('Error al obtener los clientes:', error);
      // Estado seguro en caso de error
      setClientesData(prev => ({
        ...prev,
        clientes: []
      }));
    }
  };

  useEffect(() => {
    fetchClientes(1, searchTerm);
  }, [searchTerm, filterBoatsInWater]);

  const isDateExpired = (date) => {
    if (!date) return false;
    const currentDate = new Date();
    const targetDate = new Date(date);
    return targetDate < currentDate;
  };

  const paginateNext = () => {
    if (clientesData.currentPage < clientesData.totalPages) {
      fetchClientes(clientesData.currentPage + 1, searchTerm);
    }
  };

  const paginatePrev = () => {
    if (clientesData.currentPage > 1) {
      fetchClientes(clientesData.currentPage - 1, searchTerm);
    }
  };

  const handleSelectBoatClick = (clientBoats) => {
    setSelectedClientBoats(clientBoats);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedClientBoats([]);
  };

  const handleNavigateToMovements = () => {
    navigate('/movimientos');
  };

  const handlePageSelect = (event) => {
    const selectedPage = Number(event.target.value);
    if (selectedPage >= 1 && selectedPage <= clientesData.totalPages) {
      fetchClientes(selectedPage, searchTerm);
    }
  };

  // Función para verificar si un cliente tiene barcos en el agua
  const hasBoatInWater = (cliente) => {
  return cliente.embarcaciones?.some(embarcacion => 
    embarcacion.movimientos?.some(movimiento => 
      movimiento.estado === 'En el agua' && 
      new Date(movimiento.fechaHoraIngreso).toDateString() === new Date().toDateString()
    )
  );
};

  return (
    <div className="min-h-screen bg-blanco-puro">
      <Navbar />
      <main className="container mx-auto mt-8 px-4">
        <h1 className="text-4xl font-bold text-azul-oscuro mb-8">Gestión de Clientes</h1>
        <div className="flex justify-center mb-8">
          <Input
            type="text"
            placeholder="Buscar clientes por nombre, id, matrícula..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md border border-gris-piedra focus:border-verde-esmeralda focus:ring-verde-esmeralda rounded-lg shadow-sm"
          />
        </div>
        <div className="flex justify-center mb-8">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filterBoatsInWater}
              onChange={(e) => setFilterBoatsInWater(e.target.checked)}
              className="mr-2"
            />
            Mostrar solo clientes con embarcaciones en el agua
          </label>
        </div>
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {clientesData.clientes.length > 0 ? (
            clientesData.clientes.map(cliente => {
              const isLicenseExpired = isDateExpired(cliente.vencimientoLicencia);
              const expiredBoats = cliente.embarcaciones?.filter(embarcacion => isDateExpired(embarcacion.vencimientoSeguro)) || [];
              const boatsWithExpiredMatricula = cliente.embarcaciones?.filter(
                (embarcacion) => isDateExpired(embarcacion.vencimientoMatricula)
              ) || [];
              const clientHasBoatInWater = hasBoatInWater(cliente);

              return (
                <motion.div
                  key={cliente.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border border-gris-piedra rounded-lg shadow-md hover:shadow-lg transition-shadow relative overflow-hidden"
                >
                  {clientHasBoatInWater && (
                    <motion.div
                      className="absolute top-0 right-0 w-16 h-16"
                      animate={{
                        rotate: [0, 10, -10, 10, 0],
                        y: [0, -5, 5, -5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse",
                      }}
                    >
                      <Ship className="w-full h-full text-blue-400" />
                    </motion.div>
                  )}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-azul-oscuro font-semibold">{cliente.nombre} {cliente.apellido}</CardTitle>
                      <CardDescription className="text-azul-oscuro text-xl font-bold">Cliente ID: {cliente.id}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-verde-esmeralda font-medium">
                        Embarcaciones: {cliente.embarcaciones ? cliente.embarcaciones.length : 0}
                      </p>
                      {isLicenseExpired && (
                        <p className="text-red-500 font-medium">Licencia vencida</p>
                      )}
                      {expiredBoats.length > 0 && (
                        <p className="text-red-500 font-medium">Embarcaciones con seguro vencido: {expiredBoats.length}</p>
                      )}
                      {boatsWithExpiredMatricula.length > 0 && (
                        <p className="text-red-500 font-medium">Matrículas vencidas: {boatsWithExpiredMatricula.length}</p>
                      )}
                      {cliente.embarcaciones && cliente.embarcaciones.length > 0 ? (
                        <ul className="list-disc pl-5 text-verde-esmeralda mt-2">
                          {cliente.embarcaciones.map((embarcacion, index) => (
                            <li key={index}>
                              Tipo: {embarcacion.tipoEmbarcacion || 'Sin tipo especificado'} - Matrícula: {embarcacion.numeroMatricula || 'Sin matrícula'}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-verde-esmeralda mt-2">No hay embarcaciones registradas.</p>
                      )}
                    </CardContent>
                    <CardFooter className="flex justify-between mt-4">
                      <Button
                        type="primary"
                        onClick={() => navigate(`/detallesclientes/${cliente.id}`)}
                      >
                        Ver detalles
                      </Button>
                      <Button
                        type="secondary"
                        onClick={handleNavigateToMovements}
                        disabled={!clientHasBoatInWater}
                      >
                        Ver movimientos
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              );
            })
          ) : (
            <p className="text-center text-gris-piedra">No se encontraron clientes.</p>
          )}
        </div>
        <div className="flex justify-center mt-8 items-center">
          <button
            onClick={paginatePrev}
            className="px-3 py-1 mx-1 rounded bg-white text-azul-oscuro border border-azul-oscuro"
            disabled={clientesData.currentPage === 1}
          >
            Anterior
          </button>
          <span className="mx-2 text-azul-oscuro">
            Página {clientesData.currentPage} de {clientesData.totalPages}
          </span>
          <input
            type="number"
            value={clientesData.currentPage}
            onChange={handlePageSelect}
            className="w-16 text-center border border-gris-piedra rounded"
            min="1"
            max={clientesData.totalPages}
          />
          <button
            onClick={paginateNext}
            className="px-3 py-1 mx-1 rounded bg-white text-azul-oscuro border border-azul-oscuro"
            disabled={clientesData.currentPage === clientesData.totalPages}
          >
            Siguiente
          </button>
        </div>
      </main>
      {isModalOpen && (
        <Modal onClose={closeModal}>
          <h2 className="text-2xl font-bold text-azul-oscuro mb-4">Embarcaciones del cliente</h2>
          <ul className="list-disc pl-5 text-gris-piedra">
            {Array.isArray(selectedClientBoats) && selectedClientBoats.length > 0 ? (
              selectedClientBoats.map((boat, index) => (
                <li key={index}>ID: {boat.id} - {boat.nombreFantasia}</li>
              ))
            ) : (
              <p>No hay embarcaciones disponibles.</p>
            )}
          </ul>
        </Modal>
      )}
    </div>
  );
};

export default Clientes;